import type { AnswerCondition } from "@/lib/form-conditions";

export type ProcessTriggerEvent = "STEP_COMPLETED" | "PROCESS_COMPLETED";
export type ProcessTriggerMode = "STATIC" | "RULES" | "REVIEWER_CHOICE";
export type TriggerApplicantSource =
  | "PARENT_APPLICANT"
  | "FROM_ANSWER"
  | "COMPLETING_REVIEWER"
  | "FIXED_USER";
export type TriggerRunStatus =
  | "AWAITING_USER"
  | "STARTED"
  | "DECLINED"
  | "EXPIRED"
  | "CANCELLED"
  | "SKIPPED";

export interface ConditionGroup {
  match: "ALL" | "ANY";
  conditions: AnswerCondition[];
}

interface TriggerUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

export interface ProcessTrigger {
  id: string;
  processId: string;
  name: string;
  event: ProcessTriggerEvent;
  sourceFormId: string | null;
  targetProcessId: string;
  mode: ProcessTriggerMode;
  conditions: ConditionGroup | null;
  applicantSource: TriggerApplicantSource;
  applicantFormId: string | null;
  applicantQuestionId: string | null;
  fixedUserId: string | null;
  message: string | null;
  allowDecline: boolean;
  dueInDays: number | null;
  reminderEveryDays: number | null;
  isActive: boolean;
  targetProcess: { id: string; name: string; status: string; archived: boolean };
  fixedUser: TriggerUser | null;
}

export type UpsertProcessTriggerInput = Omit<
  ProcessTrigger,
  "id" | "processId" | "targetProcess" | "fixedUser"
>;

export interface TriggerRun {
  id: string;
  triggerId: string | null;
  status: TriggerRunStatus;
  statusReason: string | null;
  dueAt: string | null;
  createdAt: string;
  resolvedAt: string | null;
  lastNotifiedAt: string | null;
  reminderCount: number;
  startPath: string | null;
  childApplicantProcessId: string | null;
  targetUser: TriggerUser | null;
  triggeredBy: TriggerUser | null;
  resolvedBy: TriggerUser | null;
  targetProcess: { id: string; name: string; firstFormId: string | null };
  trigger: {
    id: string;
    name: string;
    event: ProcessTriggerEvent;
    message: string | null;
    allowDecline: boolean;
  } | null;
  parentApplicantProcess: {
    id: string;
    processId: string;
    applicantId: string;
    applicationType: string;
    process: { id: string; name: string };
  };
  childApplicantProcess: {
    id: string;
    applicationType: string;
    status: string;
    createdAt: string;
  } | null;
}

export interface ApplicationTriggerLinks {
  started: TriggerRun[];
  origin: TriggerRun | null;
}

export interface ListTriggerRunsParams {
  processId?: string;
  targetProcessId?: string;
  status?: TriggerRunStatus;
  search?: string;
  page?: number;
  limit?: number;
}

/** Admin/staff detail route for an application, by its ApplicationType. */
export const managementApplicationPath = (
  base: "admin" | "staff",
  applicationType: string,
  processId: string,
  applicantProcessId: string
) => {
  const tab =
    applicationType === "COMPLETED"
      ? "completed"
      : applicationType === "DISABLED"
        ? "disabled"
        : "pending";
  const prefix = base === "admin" ? "/admin/management" : "/staff/incoming";
  return `${prefix}/${tab}-applications/${processId}/${applicantProcessId}`;
};
